import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  IconButton,
  Typography,
  Tooltip,
  TablePagination,
  CircularProgress,
  Alert
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { inviteService } from '../../services/invites';

const STATUS_COLORS = {
  pending: 'warning',
  accepted: 'success',
  expired: 'default',
  revoked: 'error'
};

const PendingInvitesPanel = ({ onRefreshNeeded }) => {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    loadInvites();
  }, [page, rowsPerPage]);

  const loadInvites = async () => {
    try {
      setLoading(true);
      const { data, count } = await inviteService.getInvites({
        limit: rowsPerPage,
        offset: page * rowsPerPage
      });
      setInvites(data || []);
      setTotalCount(count || 0);
      setError(null);
    } catch (err) {
      console.error('Error loading invites:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async (inviteId) => {
    try {
      setActionLoading(inviteId);
      await inviteService.resendInvite(inviteId);
      loadInvites();
    } catch (err) {
      console.error('Error resending invite:', err);
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRevoke = async (inviteId) => {
    try {
      setActionLoading(inviteId);
      await inviteService.revokeInvite(inviteId);
      loadInvites();
      if (onRefreshNeeded) onRefreshNeeded();
    } catch (err) {
      console.error('Error revoking invite:', err);
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleCopyLink = (token) => {
    const link = `${window.location.origin}/signup?invite=${token}`;
    navigator.clipboard.writeText(link);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isExpired = (expiresAt) => {
    return new Date(expiresAt) < new Date();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading && invites.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1">
          Invitations ({totalCount})
        </Typography>
        <Button
          size="small"
          startIcon={<RefreshIcon />}
          onClick={loadInvites}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {invites.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No invitations yet. Click "Invite User" to send the first one.
          </Typography>
        </Paper>
      ) : (
        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Invited By</TableCell>
                  <TableCell>Expires</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invites.map((invite) => {
                  const expired = isExpired(invite.expires_at) && invite.status === 'pending';
                  const effectiveStatus = expired ? 'expired' : invite.status;

                  return (
                    <TableRow key={invite.id}>
                      <TableCell>
                        <Typography variant="body2">{invite.email}</Typography>
                        {invite.competition_access?.length > 0 && (
                          <Typography variant="caption" color="text.secondary">
                            +{invite.competition_access.length} competition{invite.competition_access.length > 1 ? 's' : ''}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={invite.default_role}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={effectiveStatus}
                          size="small"
                          color={STATUS_COLORS[effectiveStatus]}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {invite.inviter?.email || 'Unknown'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          color={expired ? 'error' : 'text.secondary'}
                        >
                          {formatDate(invite.expires_at)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {invite.status === 'pending' && (
                          <>
                            <Tooltip title="Copy invite link">
                              <IconButton
                                size="small"
                                onClick={() => handleCopyLink(invite.token)}
                              >
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Resend invite">
                              <IconButton
                                size="small"
                                onClick={() => handleResend(invite.id)}
                                disabled={actionLoading === invite.id}
                              >
                                {actionLoading === invite.id ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <SendIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Revoke invite">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRevoke(invite.id)}
                                disabled={actionLoading === invite.id}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        {invite.status === 'accepted' && (
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(invite.accepted_at)}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
          />
        </Paper>
      )}
    </Box>
  );
};

export default PendingInvitesPanel;
